import { SCREENS, QUESTIONS, SECTIONS } from './questionnaire';

function evaluateCondition(condition, answers) {
  const answer = answers[condition.id];
  let retVal = answer && (condition.answers ? condition.answers.includes(answer) : true);
  if (condition.excludes) retVal = !retVal;
  return retVal;
}

function ageInYears(dateString, now = new Date()) {
  const birthDate = new Date(`${dateString}T00:00:00Z`);
  if (Number.isNaN(birthDate.getTime())) return null;
  let years = now.getUTCFullYear() - birthDate.getUTCFullYear();
  const monthDelta = now.getUTCMonth() - birthDate.getUTCMonth();
  if (monthDelta < 0 || (monthDelta === 0 && now.getUTCDate() < birthDate.getUTCDate())) {
    years -= 1;
  }
  return years;
}

function evaluateCustomConditional(condition, answers) {
  if (typeof condition === 'function') return condition(answers);
  if (condition.type === 'ageAtLeast') {
    if (answers[condition.genderQuestionId] === condition.excludedGender) return false;
    const birthDate = answers[condition.birthDateQuestionId];
    const years = birthDate ? ageInYears(birthDate) : null;
    return years !== null && years >= condition.years;
  }
  return true;
}

export function questionMeetsConditions(questionId, answers) {
  const question = QUESTIONS[questionId];
  let meetsCondition = true;
  if (question.customConditional) {
    meetsCondition = evaluateCustomConditional(question.customConditional, answers);
  }
  if (question.conditional) {
    if (question.conditional.id) {
      meetsCondition = meetsCondition && evaluateCondition(question.conditional, answers);
    } else if (question.conditional.multi?.length) {
      // possible operators are 'and' and 'atLeast', default is 'or'
      // 'atLeast' and 'or' init to false
      let conditionalMeets = question.conditional.operator === 'and' ? true : false;
      let count = 0;
      conditionalMeets = question.conditional.multi.reduce((result, current) => {
        if (question.conditional.operator === 'and') {
          result = result && evaluateCondition(current, answers);
        } else if (question.conditional.operator === 'atLeast') {
          if (evaluateCondition(current, answers)) count++;
          result = count >= question.conditional.atLeast;
        } else {
          result = result || evaluateCondition(current, answers);
        }
        return result;
      }, conditionalMeets);
      meetsCondition = meetsCondition && conditionalMeets;
    } else {
      // simple condition of single question id with single answer Yes
      meetsCondition =
        meetsCondition &&
        evaluateCondition({ id: question.conditional, answers: ['Yes'] }, answers);
    }
  }
  if (question.conditional2) {
    meetsCondition = meetsCondition && evaluateCondition(question.conditional2, answers);
  }
  return meetsCondition;
}

function questionMeetsConditionsAndAnsweredAndValid(questionId, answers) {
  const question = QUESTIONS[questionId];
  const required = question.options?.length === 1 ? false : question.required !== false;
  const meetsConditions = questionMeetsConditions(questionId, answers);

  const answer = answers[questionId];
  let answered = !!answer || question.type === 'multi';

  let valid = true;

  const subQuestionsRequired =
    question.subQuestions?.length && answer === 'Yes' && question.type === 'yesno';
  const subQuestionsRequires1Answer =
    question.subQuestions?.length && !!answer && question.type === 'check';
  const multiSubQuestionsRequired = question.subQuestions?.length && question.type === 'multi';

  if (subQuestionsRequired) {
    question.subQuestions.forEach((sq) => {
      answered = answered && !!answers[`${question.questionId}_${sq}`];
    });
  }
  if (subQuestionsRequires1Answer) {
    let sqAnswered = false;
    question.subQuestions.forEach((sq) => {
      sqAnswered = sqAnswered || !!answers[`${question.questionId}_${sq}`];
    });
    answered = answered && sqAnswered;
    if (!sqAnswered) valid = false;
  }
  if (multiSubQuestionsRequired) {
    question.subQuestions.forEach((sq) => {
      answered = answered && !!answers[`${question.questionId}_1_${sq}`];
    });
    valid = true;
  }

  valid = valid && (!required || !!answered);

  return { meetsConditions, answered, valid };
}

function processQuestionGroupOutput(requiresAnswer, groupOutput) {
  let meetsConditions = false;
  let answered = false;
  let valid = true;
  groupOutput.forEach((o) => {
    // display if any question should be displayed
    meetsConditions = meetsConditions || o.meetsConditions;
    if (o.meetsConditions) {
      valid = valid && o.valid;
      answered = answered || (o.valid && o.answered);
    }
  });
  if (requiresAnswer) valid = valid && answered;

  return { meetsConditions, valid, answered };
}

function questionGroupMeetsConditionsAndAnsweredAndValid(questionGroup, answers) {
  const requiresAnswer = questionGroup.required !== false;
  const groupOutput = questionGroup.questions.map(function (questionId) {
    return questionMeetsConditionsAndAnsweredAndValid(questionId, answers);
  });
  const { meetsConditions, answered, valid } = processQuestionGroupOutput(
    requiresAnswer,
    groupOutput
  );
  return { meetsConditions, answered, valid };
}

export function screenMeetsConditionsAndAnsweredAndValid(screenId, answers) {
  const questionScreen = SCREENS[screenId];
  const requiresAnswer = questionScreen.required !== false;
  const groupOutput = questionScreen.sections.map(function (questionGroup) {
    return questionGroupMeetsConditionsAndAnsweredAndValid(questionGroup, answers);
  });
  const { meetsConditions, answered, valid } = processQuestionGroupOutput(
    requiresAnswer,
    groupOutput
  );
  return { meetsConditions, answered, valid };
}

export function sectionsThatMeetConditions(answers) {
  return SECTIONS.filter((section) => {
    return !section.conditional || !!evaluateCondition(section.conditional, answers);
  });
}

function orderedScreens(answers) {
  const filteredSections = sectionsThatMeetConditions(answers);
  let orderedScreens = [];
  filteredSections.forEach((s) => {
    orderedScreens = orderedScreens.concat(s.screens);
  });
  return orderedScreens;
}

export function sectionForScreen(screenId) {
  return SECTIONS.find((s) => s.screens.includes(screenId));
}

export function firstUnAnsweredScreen(answers) {
  const screenId = orderedScreens(answers).find((os) => {
    const { meetsConditions, answered, valid } = screenMeetsConditionsAndAnsweredAndValid(
      os,
      answers
    );
    return meetsConditions && !answered;
  });
  const currentSection = sectionForScreen(screenId);
  return { currentScreen: SCREENS[screenId], currentSection };
}

export function nextScreen(lastScreenId, answers) {
  const screenIds = orderedScreens(answers);
  const i = lastScreenId == null ? 0 : screenIds.indexOf(lastScreenId) + 1;
  const screenId = screenIds.slice(i).find((os) => {
    const { meetsConditions, answered, valid } = screenMeetsConditionsAndAnsweredAndValid(
      os,
      answers
    );
    return !!meetsConditions;
  });
  const currentSection = sectionForScreen(screenId);
  return { currentScreen: SCREENS[screenId], currentSection };
}

export function firstScreenInSection(section, answers) {
  const screenIds = orderedScreens(answers);
  const i = screenIds.indexOf(section.screens[0]);
  const screenId = screenIds.slice(i).find((os) => {
    const { meetsConditions, answered, valid } = screenMeetsConditionsAndAnsweredAndValid(
      os,
      answers
    );
    return !!meetsConditions;
  });
  const currentSection = sectionForScreen(screenId);
  return { currentScreen: SCREENS[screenId], currentSection };
}

export function previousScreen(lastScreenId, answers) {
  const screenIds = orderedScreens(answers);
  const screensReversed = screenIds.slice().reverse();
  const i = lastScreenId == null ? 0 : screensReversed.indexOf(lastScreenId) + 1;
  const screenId = screensReversed.slice(i).find((os) => {
    const { meetsConditions, answered, valid } = screenMeetsConditionsAndAnsweredAndValid(
      os,
      answers
    );
    return !!meetsConditions;
  });
  const currentSection = sectionForScreen(screenId);
  return { currentScreen: SCREENS[screenId], currentSection };
}
